import React, { useCallback } from 'react'
import { func, number, string, shape } from 'prop-types'
import {
  button,
  text,
  list,
  paragraph,
} from '@groovehq/internal-design-system/lib/styles/elements'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import { styles as mainStyles } from 'components/integrations/ShopifyV2WidgetCard/styles'
import { shopifyMoneyString } from 'util/currency'

import { styles } from './styles'
import AdditionalInfo from './AdditionalInfo'
import PriceDiscount from '../PriceDiscount'
import AddShipping from '../AddShipping'
import SetTax from '../SetTax'

const DiscountSection = React.memo(
  ({ currency, discount, handleDiscountChange, rawTotal, orderItemsCount }) => {
    return (
      <>
        <PriceDiscount
          currency={currency}
          discount={discount}
          onDiscountChange={handleDiscountChange}
          price={rawTotal}
          isOrder
          disabled={orderItemsCount === 0}
        >
          <button css={button.styles.link} type="button">
            Add discount
          </button>
        </PriceDiscount>
        <span>
          {!discount.isPercent &&
            discount.number > 0 && (
              <span css={mainStyles.moneyString}>
                −{shopifyMoneyString({
                  currencyCode: currency,
                  amount: discount.number,
                })}
              </span>
            )}
          {discount.isPercent && discount.number > 0 && `−${discount.number}%`}
          {!discount && <>&mdash;</>}
        </span>
      </>
    )
  }
)

const ShippingSection = React.memo(
  ({ currency, shipping, handleShippingChange, calculatedDraft }) => {
    return (
      <>
        <AddShipping
          currency={currency}
          shipping={shipping}
          onShippingChange={handleShippingChange}
          calculatedDraft={calculatedDraft}
        >
          <button css={button.styles.link} type="button">
            Add shipping
          </button>
        </AddShipping>
        <span>
          {shipping.fee > 0 ? (
            <span css={mainStyles.moneyString}>
              {shopifyMoneyString({
                currencyCode: currency,
                amount: shipping.fee,
              })}
            </span>
          ) : (
            <>&mdash;</>
          )}
        </span>
      </>
    )
  }
)

const TaxesSection = React.memo(
  ({ currency, taxes, hasTaxes, setHasTaxes }) => {
    return (
      <>
        <SetTax hasTaxes={hasTaxes} onHasTaxesChange={setHasTaxes}>
          <button css={button.styles.link} type="button">
            Taxes
          </button>
        </SetTax>
        <span>
          <span css={mainStyles.moneyString}>
            {shopifyMoneyString({ currencyCode: currency, amount: taxes })}
          </span>
        </span>
      </>
    )
  }
)

const OrderSummary = ({
  className,
  orderItemsCount,
  currency,
  rawTotal,
  onSendInvoice,
  canSendInvoice,
  hasTaxes,
  setHasTaxes,
  discount,
  setDiscount,
  shipping,
  setShipping,
  totalTax,
  totalPrice,
  subtotalPrice,
  invoiceSentAt,
  control,
  updateDraft,
  calculatedDraft,
}) => {
  const handleDiscountChange = useCallback(
    change => {
      if (change === false) return setDiscount(change)
      return setDiscount({ ...discount, ...change })
    },
    [discount, setDiscount]
  )

  const handleShippingChange = useCallback(
    change => {
      setShipping(change)
    },
    [setShipping]
  )

  return (
    <>
      <div
        className={['grui flex justify-between', className]}
        css={styles.row}
      >
        <AdditionalInfo
          className="grui flex-1"
          control={control}
          updateDraft={updateDraft}
        />
        <div className="grui text-right mr-12 ml-16">
          <ul
            className="grui pb-12 mb-12"
            css={[list.styles.preflight, styles.summary]}
          >
            <li>
              <DiscountSection
                currency={currency}
                discount={discount}
                handleDiscountChange={handleDiscountChange}
                rawTotal={rawTotal}
                orderItemsCount={orderItemsCount}
              />
            </li>
            <li>
              <span css={text.styles.textMediumDark}>Subtotal</span>
              <span css={mainStyles.moneyString}>
                {shopifyMoneyString({
                  currencyCode: currency,
                  amount: subtotalPrice || 0,
                })}
              </span>
            </li>
            <li>
              <ShippingSection
                calculatedDraft={calculatedDraft}
                currency={currency}
                shipping={shipping}
                handleShippingChange={handleShippingChange}
              />
            </li>
            <li>
              <TaxesSection
                currency={currency}
                taxes={totalTax}
                hasTaxes={hasTaxes}
                setHasTaxes={setHasTaxes}
              />
            </li>
            <li css={text.styles.fontMedium}>
              <span css={text.styles.textMediumDark}>Total</span>
              <span css={mainStyles.moneyString}>
                {shopifyMoneyString({
                  currencyCode: currency,
                  amount: totalPrice || 0,
                })}
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div className="grui text-right mr-12">
        {invoiceSentAt ? (
          <div className="grui inline-block">
            <p css={[paragraph.styles.preflight, text.styles.textMediumDark]}>
              <span css={text.styles.fontMedium}>Invoice sent</span>
              <> on </>
              <time>{invoiceSentAt}</time>
            </p>
            <button
              type="button"
              css={button.styles.link}
              disabled={!canSendInvoice}
              onClick={onSendInvoice}
            >
              Resend invoice
            </button>
          </div>
        ) : (
          <Button
            size="small"
            disabled={!canSendInvoice}
            onClick={onSendInvoice}
          >
            Send invoice
          </Button>
        )}
      </div>
    </>
  )
}

OrderSummary.propTypes = {
  orderItemsCount: number,
  currency: string,
  rawTotal: number,
  onCloseCreateOrder: func,
  onSendInvoice: func,
  invoice: shape({
    to: shape({ email: string }),
    from: shape({ email: string, name: string }),
    subject: string,
  }),
  className: string,
}

OrderSummary.defaultProps = {
  orderItemsCount: 0,
  currency: '',
  rawTotal: 0,
  onCloseCreateOrder() {},
  onSendInvoice() {},
  invoice: {},
  className: undefined,
}

export default OrderSummary
